import { IconAttributeTraveller } from '@components/icons/attribute-traveller';
import { IconPhone } from '@components/icons/phone';
import { PhoneField } from '@components/phone-field';
import { TextField } from '@components/text-field';
import { getScopedTranslate } from '@locale/translate';
import { asteriskLabel } from '@utils/strings';

import { FieldDropzone } from './field-dropzone';

import { conditionsTab } from '../conditions-tab';
import { useFormBuilderContext } from '../context';
import { getBuilderDisplayDefault } from '../default-value-input';
import { DefaultValueMapping, DefaultValueMappingDisplay } from '../default-value-mapping';
import { DescriptionInput } from '../description-input';
import { FieldEdit } from '../field-edit';
import { FieldConfig } from '../field-registry';
import { FieldSidebarLabel } from '../fields-sidebar';
import { RequiredSwitch } from '../required-switch';

type Config = FieldConfig<'input-phone'>;

const Display: Config['Display'] = ({ field }) => {
  const displayDefault = getBuilderDisplayDefault(field);
  const hasVariableMapping =
    field.mappedDefaultValue?.variables &&
    Object.keys(field.mappedDefaultValue.variables).length > 0;

  if (hasVariableMapping) {
    return (
      <DefaultValueMappingDisplay field={field}>
        <TextField
          label={asteriskLabel(field.label, field.isRequired)}
          description={field.description}
          labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
        />
      </DefaultValueMappingDisplay>
    );
  }

  return (
    <PhoneField
      key={displayDefault ?? 'empty'}
      label={asteriskLabel(field.label, field.isRequired)}
      description={field.description}
      labelIcon={field.companyAttributeId ? <IconAttributeTraveller size={16} /> : undefined}
      defaultValue={displayDefault}
    />
  );
};

const FormBuilderPhone: Config['Component'] = ({ field, dragHandle }) => {
  const actions = useFormBuilderContext().store.actions;

  return (
    <FieldDropzone field={field}>
      <FieldEdit
        field={field}
        dragHandle={dragHandle}
        display={<Display field={field} />}
        edit={
          <>
            <TextField
              onChange={actions.createChangeHandler(field, 'label')}
              defaultValue={field.label}
              label={t('label')}
              maxChars={255}
            />
            <DescriptionInput field={field} />
            <RequiredSwitch field={field} />
            <DefaultValueMapping field={field} />
          </>
        }
      />
    </FieldDropzone>
  );
};

const { t } = getScopedTranslate({
  en: {
    title: 'Phone number',
    label: 'Label',
  },
});

export const phoneConfig: Config = {
  kind: 'input-phone',
  title: t('title'),
  group: 'inputs',
  propertiesTabs: [conditionsTab],
  Display,
  TreeDisplay: ({ field }) => <FieldSidebarLabel label={field.label} />,
  Icon: IconPhone,
  Component: FormBuilderPhone,
};
